import { anything, instance, mock, when } from 'ts-mockito';
import { I18N } from 'aurelia-i18n';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ApplePay } from './apple-pay';
import { HpfDigitalWalletService } from '../../../../services/hpf-digital-wallet.service';
import { ServiceResponse } from '../../../../models/service-response';
import { HpfFeeService } from '../../../../services/hpf-fee.service';
import { ApplePayValidator } from './apple-pay-validator';
import { ApplePayLineItemBuilder, ApplePayPaymentRequestBuilder, ApplePaySettings } from '../../../../models/digital-wallet';
import { State } from '../../../../services/global-state';
import { PayrazrService } from '../../../../services/payrazr';
import { MerchantConsumerPortalFeeSettings } from '../../../../models/merchant-consumer-portal-fee-settings';
import { PaymentPlanSettings } from '../../../../models/payment-plan-settings';
import { PaymentPlanType } from '../../../../models/paymentPlanType';

describe('NewPortalWeb', () => {
	describe('components', () => {
		describe('Apple Pay', () => {
			let component: ApplePay;

			// create mocks
			const mockElement: Element = mock(Element);
			const mockHpfDigitalWalletService: HpfDigitalWalletService = mock(HpfDigitalWalletService);
			const mockHpfFeeService: HpfFeeService = mock(HpfFeeService);
			const mockPayrazrService: PayrazrService = mock(PayrazrService);
			const mockEventAggregator: EventAggregator = mock(EventAggregator);
			const mockI18N: I18N = mock(I18N);
			const mockState: State = mock(State);
			// there are currently 14 versions of ApplePayJS; use +1 to avoid invalid 0 version
			let mockVersion: number = (Math.floor(Math.random() * 13)) + 1;

			// real instance of validator to call validate payment request
			let applePayValidator: ApplePayValidator = new ApplePayValidator(instance(mockI18N));

			// mock session
			let applePaySession: ApplePaySession = {
				begin: () => true,
				oncancel: () => true,
				onpaymentauthorized: () => true,
				onpaymentmethodselected: () => true,
				onshippingcontactselected: () => true,
				onshippingmethodselected: () => true,
				onvalidatemerchant: () => true,
				abort: () => true,
				completeMerchantValidation: () => true,
				completePayment: () => true,
				completePaymentMethodSelection: () => true,
				completeShippingContactSelection: () => true,
				completeShippingMethodSelection: () => true,
				addEventListener: () => true,
				dispatchEvent: () => true,
				removeEventListener: () => true
			};

			// mock payment method selected event
			const mockApplePayPaymentMethodSelectedEvent: ApplePayJS.ApplePayPaymentMethodSelectedEvent = {
				paymentMethod: {
					displayName: 'test user',
					network: 'masterCard',
					type: 'credit',
					paymentPass: null
				},
				bubbles: true,
				cancelable: true,
				cancelBubble: true,
				composed: true,
				currentTarget: null,
				defaultPrevented: false,
				eventPhase: 0,
				isTrusted: true,
				returnValue: true,
				srcElement: null,
				target: null,
				timeStamp: null,
				type: null,
				composedPath: null,
				initEvent: null,
				preventDefault: null,
				stopImmediatePropagation: null,
				stopPropagation: null
			};

			beforeEach(() => {
				component = new ApplePay(
					instance(mockElement),
					instance(mockHpfDigitalWalletService),
					instance(mockHpfFeeService),
					instance(mockPayrazrService),
					instance(mockEventAggregator),
					applePayValidator,
					instance(mockI18N)
				);

				// create mock session
				component.session = applePaySession;
				component.setApplePaySessionEventHandlers();
				spyOn(component.session, 'begin');
				spyOn(component.session, 'completeMerchantValidation');
				spyOn(component.session, 'completePaymentMethodSelection');
				spyOn(component.session, 'abort');

				component.merchantConsumerPortalFeeSettings = new MerchantConsumerPortalFeeSettings();
				component.merchantConsumerPortalFeeSettings.feesEnabled = false;

				component.paymentPlanSettings = new PaymentPlanSettings();
				component.paymentPlanSettings.paymentPlanType = PaymentPlanType.NotSet;

				// apple specific settings
				component.applePaySettings = new ApplePaySettings(mockI18N, mockState);
				component.applePaySettings.totalAmount = (Math.floor(Math.random() * 1000)).toString();
				component.applePaySettings.acceptsCredit = true;
				component.applePaySettings.acceptsDebit = true;
				component.applePaySettings.hasValidVersion = () => mockVersion > 0;
				component.applePaySettings.supportedNetworks = ['mastercard', 'discover', 'amex', 'cartesbancaires', 'chinaunionpay',
					'eftpos', 'electron', 'elo', 'girocard', 'inerac', 'jcb', 'mada', 'maestro', 'mir', 'privatelabel', 'visa', 'vpay'];

				// build mock payment request
				component.applePayPaymentRequest = component.getApplePayPaymentRequest();

				// component level settings
				component.hasCorrectSignature = true;
				component.hasAcceptedTerms = true;
				component.hasCorrectSignature = true;
				component.hasAcceptedTerms = true;

				// localization
				const i18next = {
					t: (a, b) => a
				};
				when(mockI18N.i18next).thenReturn(i18next);
				spyOn(i18next, 't').and.returnValue('localized');
			});

			describe('attached()', () => {
				beforeEach(() => {
					when(mockHpfDigitalWalletService.getDigitalWalletServiceSettings())
						.thenResolve({ isSuccessful: true, value: { applePayJsVersion: mockVersion } } as ServiceResponse);
				});

				it('should set canMakePayments to true when canMakePayments and supportsVersion', async () => {
					// act
					await component.attached();

					// assert
					expect(component.applePaySettings.version).toBe(mockVersion);
				});
			});

			describe('onApplePayButtonClicked()', () => {
				beforeEach(() => {
					spyOn(component, 'getApplePaySession').and.returnValue(applePaySession);
					spyOn(component, 'setApplePaySessionEventHandlers');
					spyOn(component, 'publishApplePayValidationErrors');
					spyOn(applePayValidator, 'validatePaymentRequest').and.callThrough();
				});

				it('should begin ApplePayWebSession', () => {
					// arrange
					spyOn(component, 'canMakeApplePayPayments').and.returnValue(true);

					// act
					component.onApplePayButtonClicked();

					// assert
					expect(component.getApplePaySession).toHaveBeenCalledTimes(1);
					expect(component.setApplePaySessionEventHandlers).toHaveBeenCalledTimes(1);
					expect(applePaySession.begin).toHaveBeenCalledTimes(1);
					expect(component.publishApplePayValidationErrors).toHaveBeenCalledTimes(0);
				});

				it('should publish error message and return when canMakeApplePayPayments is false', () => {
					// arrange
					spyOn(component, 'canMakeApplePayPayments').and.returnValue(false);

					// act
					component.onApplePayButtonClicked();

					// assert
					expect(component.publishApplePayValidationErrors).toHaveBeenCalledTimes(1);
					expect(component.getApplePaySession).toHaveBeenCalledTimes(0);
					expect(component.setApplePaySessionEventHandlers).toHaveBeenCalledTimes(0);
					expect(component.publishApplePayValidationErrors).toHaveBeenCalledWith(['localized']);
				});

				it('should publish error message and return when processor does not support debit nor credit', () => {
					// arrange
					spyOn(component, 'canMakeApplePayPayments').and.returnValue(true);
					component.applePaySettings.acceptsCredit = false;
					component.applePaySettings.acceptsDebit = false;

					// act
					component.onApplePayButtonClicked();

					// assert
					expect(component.publishApplePayValidationErrors).toHaveBeenCalledTimes(1);
					expect(component.getApplePaySession).toHaveBeenCalledTimes(0);
					expect(component.setApplePaySessionEventHandlers).toHaveBeenCalledTimes(0);
					expect(component.publishApplePayValidationErrors).toHaveBeenCalledWith(['localized']);
				});

				it('should publish error message and return when totalAmount is less than 1 and paymen type is final', () => {
					// arrange
					const mockBadTotal = new ApplePayLineItemBuilder()
						.setLabel()
						.setAmount('0')
						.setType('final')
						.getApplePayLineItem();

					const mockRequest = new ApplePayPaymentRequestBuilder(component.applePaySettings)
						.setTotal(mockBadTotal)
						.setCountryCode()
						.setCurrencyCode()
						.setMerchantCapabilites()
						.setSupportedNetworks()
						.setRequiredBillingContactFields()
						.getApplePayPaymentRequest();

					spyOn(component, 'canMakeApplePayPayments').and.returnValue(true);
					spyOn(component, 'getApplePayPaymentRequest').and.returnValue(mockRequest);

					// act
					component.onApplePayButtonClicked();

					// assert
					expect(component.publishApplePayValidationErrors).toHaveBeenCalledTimes(1);
					expect(component.getApplePaySession).toHaveBeenCalledTimes(0);
					expect(component.setApplePaySessionEventHandlers).toHaveBeenCalledTimes(0);
					expect(component.publishApplePayValidationErrors).toHaveBeenCalledWith(['localized']);
				});

				it('should publish error message and return when supportedNewtorks are invalid', () => {
					// arrange
					spyOn(component, 'canMakeApplePayPayments').and.returnValue(true);
					component.applePaySettings.supportedNetworks = [];

					// act
					component.onApplePayButtonClicked();

					// assert
					expect(component.publishApplePayValidationErrors).toHaveBeenCalledTimes(1);
					expect(component.getApplePaySession).toHaveBeenCalledTimes(0);
					expect(component.setApplePaySessionEventHandlers).toHaveBeenCalledTimes(0);
					expect(component.publishApplePayValidationErrors).toHaveBeenCalledWith(['localized: a valid network is required']);
				});
			});

			describe('setApplePaySessionEventHandlers()', () => {
				it('should set Apple Pay Session event handlers', () => {
					// arrange
					component.session.onvalidatemerchant = null;
					component.session.onpaymentmethodselected = null;
					component.session.onpaymentauthorized = null;
					component.session.oncancel = null;

					// act
					component.setApplePaySessionEventHandlers();

					// assert
					expect(component.session.onvalidatemerchant).not.toBeNull();
					expect(component.session.onpaymentmethodselected).not.toBeNull();
					expect(component.session.onpaymentauthorized).not.toBeNull();
					expect(component.session.oncancel).not.toBeNull();
				});
			});

			describe('ApplePaySession Event Handlers', () => {
				beforeEach(() => {
					spyOn(component, 'publishApplePayValidationErrors');
				});

				describe('onvalidatemerchant()', () => {
					it('should succeed and completeMerchantValidation()', async () => {
						// arrange
						const encodedMockSession: string = btoa('{ "apple_pay_session_object": "somelongsessionstringfromdws" }');
						when(mockHpfDigitalWalletService.createApplePayWebSession(anything()))
							.thenResolve({ isSuccessful: true, value: encodedMockSession } as ServiceResponse);

						// act
						await component.session.onvalidatemerchant(anything());

						// assert
						expect(component.session.completeMerchantValidation).toHaveBeenCalledTimes(1);
						expect(component.publishApplePayValidationErrors).toHaveBeenCalledTimes(0);
					});

					it('should fail and abort()', async () => {
						// arrange
						when(mockHpfDigitalWalletService.createApplePayWebSession(anything()))
							.thenResolve({ isSuccessful: false, value: null } as ServiceResponse);

						// act
						await component.session.onvalidatemerchant(anything());

						// assert
						expect(component.session.abort).toHaveBeenCalledTimes(1);
						expect(component.publishApplePayValidationErrors).toHaveBeenCalledTimes(1);
						expect(component.session.completeMerchantValidation).toHaveBeenCalledTimes(0);
					});
				});

				describe('onpaymentmethodselected()', () => {
					beforeEach(() => {
						spyOn(applePayValidator, 'validatePaymentMethodType').and.returnValue([]);
						spyOn(applePayValidator, 'validatePostalCode').and.returnValue([]);
					});
					describe('Installment: handleInstallmentPlanNewPaymentMethodSelected()', () => {
						beforeEach(() => {
							component.paymentPlanSettings.paymentPlanType = PaymentPlanType.Installment;
							const currentDate = new Date();
							const totalAmount: number = Math.floor(Math.random() * 1000);
							const recurringAmount: number = Math.floor(Math.random() * 1000);
							const numberOfPayments: number = Math.floor(Math.random() * 10);
							const firstPayment: number = totalAmount / numberOfPayments;
							component.paymentPlanSettings.installment = {
								payToday: false,
								nextPaymentDate: currentDate.setDate(currentDate.getDate() + 1),
								selectedFrequency: 1,
								totalAmount: totalAmount.toString(),
								number: numberOfPayments,
								firstPayment,
								paymentAfterFirst: firstPayment,
								discountPercent: 0
							};

							component.applePaySettings.recurringAmount = recurringAmount.toString();
							component.applePayPaymentRequest = component.getApplePayPaymentRequest();
						});
						it('should succeed and call completePaymentMethodSelection() when fees are disabled', async () => {
							// arrange
							component.merchantConsumerPortalFeeSettings.feesEnabled = false;

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(1);
							expect(component.session.abort).toHaveBeenCalledTimes(0);
						});

						it('should succeed and call completePaymentMethodSelection() when fees are enabled', async () => {
							// arrange
							component.paymentPlanSettings.paymentPlanType = PaymentPlanType.Installment;
							component.merchantConsumerPortalFeeSettings.feesEnabled = true;
							component.merchantConsumerPortalFeeSettings.allowFeesOnPaymentPlans = true;
							const plannedPayments = [{ billAmount: 100, planedServiceFee: 1 }, { billAmount: 100, planedServiceFee: 1 }];

							when(mockPayrazrService.computePlannedPayments(
								anything(),
								anything(),
								anything(),
								anything(),
								anything(),
								anything(),
								anything(),
								anything(),
								anything()))
								.thenResolve({ plannedPayments });

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.calculatedServiceFee).toBe(plannedPayments[0].planedServiceFee);
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(1);
							expect(component.session.abort).toHaveBeenCalledTimes(0);
						});

						it('should fail and call abort() when compute plans fails', async () => {
							// arrange
							component.paymentPlanSettings.paymentPlanType = PaymentPlanType.Installment;
							component.merchantConsumerPortalFeeSettings.feesEnabled = true;
							component.merchantConsumerPortalFeeSettings.allowFeesOnPaymentPlans = true;

							when(mockPayrazrService.computePlannedPayments(
								anything(),
								anything(),
								anything(),
								anything(),
								anything(),
								anything(),
								anything(),
								anything(),
								anything()))
								.thenReject(anything())
								.thenCall(() => {
									expect(component.publishApplePayValidationErrors).toHaveBeenCalledTimes(1);
									expect(component.session.abort).toHaveBeenCalledTimes(1);
								});

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(0);
						});
					});
					describe('Recurring: handleRecurringPlanNewPaymentMethodSelected()', () => {
						beforeEach(() => {
							const currentDate = new Date();
							const firstPaymentDate = currentDate.setDate(currentDate.getDate() + 1);
							const lastPaymentDate = currentDate.setDate(currentDate.getDate() + 2);
							const recurringAmount: number = Math.floor(Math.random() * 1000);
							component.paymentPlanSettings.paymentPlanType = PaymentPlanType.Recurring;
							component.paymentPlanSettings.recurring = {
								firstPaymentDate: firstPaymentDate.toString(),
								frequency: 1,
								lastPaymentDate: lastPaymentDate.toString(),
								numberOfRetriesForCardDeclines: 0,
								paymentAmount: (Math.floor(Math.random() * 10)).toString()
							};

							component.applePaySettings.recurringAmount = recurringAmount.toString();
							component.applePayPaymentRequest = component.getApplePayPaymentRequest();
						});
						it('should succeed and call completePaymentMethodSelection() when fees are disabled', async () => {
							// arrange
							component.merchantConsumerPortalFeeSettings.feesEnabled = false;

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(1);
							expect(component.session.abort).toHaveBeenCalledTimes(0);
						});

						it('should succeed and call completePaymentMethodSelection() when fees are enabled', async () => {
							// arrange
							component.merchantConsumerPortalFeeSettings.feesEnabled = true;
							component.merchantConsumerPortalFeeSettings.allowFeesOnPaymentPlans = true;

							const feeValue = (Math.floor(Math.random() * 100));
							when(mockHpfFeeService.calculateFee(anything()))
								.thenResolve({ isSuccessful: true, value: { convenienceFee: feeValue } } as ServiceResponse);

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.calculatedServiceFee).toBe(feeValue);
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(1);
							expect(component.session.abort).toHaveBeenCalledTimes(0);
						});

						it('should fail and call abort() when calculate fee fails', async () => {
							// arrange
							component.merchantConsumerPortalFeeSettings.feesEnabled = true;
							component.merchantConsumerPortalFeeSettings.allowFeesOnPaymentPlans = true;

							await when(mockHpfFeeService.calculateFee(anything()))
								.thenReject(anything())
								.thenCall(() => {
									expect(component.publishApplePayValidationErrors).toHaveBeenCalledTimes(1);
									expect(component.session.abort).toHaveBeenCalledTimes(1);
								});

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(0);
						});
					});
					describe('Single Payment: handleSinglePayNewPaymentMethodSelected()', () => {
						it('should succeed and call completePaymentMethodSelection() when fees are disabled', async () => {
							// arrange
							component.paymentPlanSettings.paymentPlanType = PaymentPlanType.NotSet;
							component.merchantConsumerPortalFeeSettings.feesEnabled = false;
							mockApplePayPaymentMethodSelectedEvent.paymentMethod.type = 'credit';

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(1);
							expect(component.session.abort).toHaveBeenCalledTimes(0);
						});

						it('should succeed and call completePaymentMethodSelection() when fees are enabled', async () => {
							// arrange
							component.paymentPlanSettings.paymentPlanType = PaymentPlanType.NotSet;
							component.merchantConsumerPortalFeeSettings.feesEnabled = true;

							const feeValue = (Math.floor(Math.random() * 100));
							when(mockHpfFeeService.calculateFee(anything()))
								.thenResolve({ isSuccessful: true, value: { convenienceFee: feeValue } } as ServiceResponse);

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.calculatedServiceFee).toBe(feeValue);
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(1);
							expect(component.session.abort).toHaveBeenCalledTimes(0);
						});

						it('should fail and call abort() when calcualte fee fails', async () => {
							// arrange
							component.paymentPlanSettings.paymentPlanType = PaymentPlanType.NotSet;
							component.merchantConsumerPortalFeeSettings.feesEnabled = true;

							when(mockHpfFeeService.calculateFee(anything()))
								.thenResolve({ isSuccessful: false, value: null } as ServiceResponse);

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.session.abort).toHaveBeenCalledTimes(1);
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(0);
						});
					});
					describe('Single Future: handleSinglePayNewPaymentMethodSelected()', () => {
						beforeEach(() => {
							component.applePaySettings.paymentTiming = 'deferred';
						});

						it('should succeed and call completePaymentMethodSelection() when fees are disabled', async () => {
							// arrange
							component.paymentPlanSettings.paymentPlanType = PaymentPlanType.NotSet;
							component.merchantConsumerPortalFeeSettings.feesEnabled = false;
							mockApplePayPaymentMethodSelectedEvent.paymentMethod.type = 'credit';

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(1);
							expect(component.session.abort).toHaveBeenCalledTimes(0);
						});

						it('should succeed and call completePaymentMethodSelection() when fees are enabled', async () => {
							// arrange
							component.paymentPlanSettings.paymentPlanType = PaymentPlanType.NotSet;
							component.merchantConsumerPortalFeeSettings.feesEnabled = true;

							const feeValue = (Math.floor(Math.random() * 100));
							when(mockHpfFeeService.calculateFee(anything()))
								.thenResolve({ isSuccessful: true, value: { convenienceFee: feeValue } } as ServiceResponse);

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.calculatedServiceFee).toBe(feeValue);
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(1);
							expect(component.session.abort).toHaveBeenCalledTimes(0);
						});

						it('should fail and call abort() when calcualte fee fails', async () => {
							// arrange
							component.paymentPlanSettings.paymentPlanType = PaymentPlanType.NotSet;
							component.merchantConsumerPortalFeeSettings.feesEnabled = true;

							when(mockHpfFeeService.calculateFee(anything()))
								.thenResolve({ isSuccessful: false, value: null } as ServiceResponse);

							// act
							await component.session.onpaymentmethodselected(mockApplePayPaymentMethodSelectedEvent);

							// assert
							expect(component.session.completePaymentMethodSelection).toHaveBeenCalledTimes(0);
							expect(component.session.abort).toHaveBeenCalledTimes(1);
						});
					});
				});

				describe('onpaymentauthorized()', () => {
					it('should succeed and call dispatchCustomEvent()', async () => {
						// arrange
						spyOn(component, 'dispatchCustomEvent');
						spyOn(applePayValidator, 'validatePaymentRequest').and.returnValue([]);
						spyOn(applePayValidator, 'validateBillingContact').and.returnValue([]);

						// act
						await component.session.onpaymentauthorized(anything());

						// assert
						expect(component.dispatchCustomEvent).toHaveBeenCalledTimes(1);
						expect(component.session.abort).toHaveBeenCalledTimes(0);
					});
				});
			});
		});
	});
});
